'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { notificationTemplateListMachine } from './machines/notificationTemplateListMachine'
import type {
  NotificationTemplate,
  NotificationTrigger,
  NotificationType,
  SentNotification,
} from './types'

export type NotificationTemplateListMachineApi = ReturnType<
  typeof useMachine<typeof notificationTemplateListMachine>
>

const NotificationTemplateListMachineContext =
  createContext<NotificationTemplateListMachineApi | null>(null)

export function NotificationTemplateListMachineProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const api = useMachine(notificationTemplateListMachine)
  return (
    <NotificationTemplateListMachineContext.Provider value={api}>
      {children}
    </NotificationTemplateListMachineContext.Provider>
  )
}

export function useNotificationTemplateListMachine() {
  const ctx = useContext(NotificationTemplateListMachineContext)
  if (!ctx) {
    throw new Error(
      'useNotificationTemplateListMachine must be used within NotificationTemplateListMachineProvider'
    )
  }
  return ctx
}

export function useNotificationTemplateListMachineOptional() {
  return useContext(NotificationTemplateListMachineContext)
}

/**
 * Adapter: returns store-compatible interface.
 */
export function useNotificationTemplateListStoreCompat() {
  const machineApi = useNotificationTemplateListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const templates = state.context.templates
    const sentNotifications = state.context.sentNotifications
    const selectedTemplateId = state.context.selectedTemplateId

    const getTemplateById = (id: string) => templates.find((t) => t.id === id)
    const getTemplatesByType = (type: NotificationType) =>
      templates.filter((t) => t.type === type && t.isActive)
    const getTemplateByTrigger = (trigger: NotificationTrigger, type?: NotificationType) => {
      const filtered = templates.filter(
        (t) => t.trigger === trigger && t.isActive && (type ? t.type === type : true)
      )
      const defaultT = filtered.find((t) => t.isDefault)
      return defaultT || filtered[0]
    }
    const getActiveTemplates = () => templates.filter((t) => t.isActive)
    const getSentNotificationsByTicket = (ticketId: string) =>
      sentNotifications.filter((n) => n.ticketId === ticketId)
    const getSentNotificationsByCustomer = (customerId: string) =>
      sentNotifications.filter((n) => n.customerId === customerId)

    return {
      templates,
      sentNotifications,
      selectedTemplateId,
      setTemplates: (t: NotificationTemplate[]) => send({ type: 'SET_TEMPLATES', templates: t }),
      addTemplate: (template: NotificationTemplate) => send({ type: 'ADD_TEMPLATE', template }),
      updateTemplate: (id: string, updates: Partial<NotificationTemplate>) =>
        send({ type: 'UPDATE_TEMPLATE', id, updates }),
      deleteTemplate: (id: string) => send({ type: 'DELETE_TEMPLATE', id }),
      setSelectedTemplateId: (id: string | null) => send({ type: 'SET_SELECTED_TEMPLATE_ID', id }),
      getTemplateById,
      getTemplatesByType,
      getTemplateByTrigger,
      getActiveTemplates,
      addSentNotification: (notification: SentNotification) =>
        send({ type: 'ADD_SENT_NOTIFICATION', notification }),
      updateSentNotification: (id: string, updates: Partial<SentNotification>) =>
        send({ type: 'UPDATE_SENT_NOTIFICATION', id, updates }),
      getSentNotificationsByTicket,
      getSentNotificationsByCustomer,
      incrementTemplateUsage: (templateId: string) => {
        const t = machineApi[0].context.templates.find((x) => x.id === templateId)
        if (t) {
          send({
            type: 'UPDATE_TEMPLATE',
            id: templateId,
            updates: {
              usageCount: t.usageCount + 1,
              lastUsedAt: Date.now(),
            },
          })
        }
      },
    }
  }, [machineApi])
}

export function useNotificationTemplateActions() {
  const machineApi = useNotificationTemplateListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setTemplates: (templates: NotificationTemplate[]) =>
      send({ type: 'SET_TEMPLATES', templates }),
    addTemplate: (template: NotificationTemplate) => send({ type: 'ADD_TEMPLATE', template }),
    updateTemplate: (id: string, updates: Partial<NotificationTemplate>) =>
      send({ type: 'UPDATE_TEMPLATE', id, updates }),
    deleteTemplate: (id: string) => send({ type: 'DELETE_TEMPLATE', id }),
    setSelectedTemplateId: (id: string | null) => send({ type: 'SET_SELECTED_TEMPLATE_ID', id }),
    addSentNotification: (notification: SentNotification) =>
      send({ type: 'ADD_SENT_NOTIFICATION', notification }),
    updateSentNotification: (id: string, updates: Partial<SentNotification>) =>
      send({ type: 'UPDATE_SENT_NOTIFICATION', id, updates }),
  }), [send])
}

/** Helper hook to get template by trigger and type */
export function useTemplateForTrigger(trigger: NotificationTrigger, type?: NotificationType) {
  const compat = useNotificationTemplateListStoreCompat()
  return useMemo(() => compat.getTemplateByTrigger(trigger, type), [compat, trigger, type])
}

/** Helper hook to count sent notifications */
export function useSentNotificationCount(ticketId?: string, customerId?: string) {
  const compat = useNotificationTemplateListStoreCompat()
  return useMemo(() => {
    if (ticketId) return compat.getSentNotificationsByTicket(ticketId).length
    if (customerId) return compat.getSentNotificationsByCustomer(customerId).length
    return compat.sentNotifications.length
  }, [compat, ticketId, customerId])
}
